/**
 * KinsCircle Phase 6B — AI Cost Guardrails
 *
 * STRICTLY ADDITIVE. ADVISORY. Owner/admin-only surface.
 * Honors STABILITY.md §12.3.
 *
 * Reads the existing local AI usage log (recorded by aiMemoryService.ts) and
 * derives:
 *   - estimated tokens per day / month
 *   - estimated cost per day / month (using a static $/1K-tokens rate)
 *   - failure rate (recent window)
 *   - average response time
 *   - usage spike detection (today vs 7-day average)
 *
 * NEVER queries OpenAI directly. NEVER hits Supabase. NEVER stores prompts.
 * Cost numbers are heuristic estimates only and are clearly labeled as such
 * in the diagnostics card.
 */

import { readAIUsageLog, type AIUsageEntry } from './aiMemoryService';

// gpt-4o-mini / gpt-4.1-mini current public per-1K rates (Mar 2025).
// These are intentionally conservative HIGH estimates so the warning fires
// before real bills do.
const COST_PER_1K_INPUT_USD = 0.00015;
const COST_PER_1K_OUTPUT_USD = 0.0006;
// We don't separate input vs output in the log; assume ~30% input / 70% output
// for memory-style generations.
const BLENDED_COST_PER_1K_USD =
  COST_PER_1K_INPUT_USD * 0.3 + COST_PER_1K_OUTPUT_USD * 0.7;

const DAY_MS = 24 * 60 * 60 * 1000;
const MONTH_MS = 30 * DAY_MS;
const WEEK_MS = 7 * DAY_MS;

// Owner/admin warning thresholds. These are advisory and tuned to alert
// BEFORE real-world spend matters.
export const AI_COST_DAILY_WARN_USD = 1.0;
export const AI_COST_MONTHLY_WARN_USD = 20.0;
export const AI_FAILURE_RATE_WARN_PCT = 25;
export const AI_USAGE_SPIKE_MULTIPLIER = 4;

export type AICostStats = {
  totalCalls: number;
  totalCallsToday: number;
  totalCallsMonth: number;
  totalTokens: number;
  estDailyCostUsd: number;
  estMonthlyCostUsd: number;
  failureRatePct: number;
  avgDurationMs: number;
  topErrorCode: string | null;
  spikeDetected: boolean;
  warnings: AICostWarning[];
};

export type AICostWarning = {
  level: 'warn' | 'critical';
  code: 'daily_cost' | 'monthly_cost' | 'failure_rate' | 'usage_spike';
  message: string;
};

const sumTokens = (entries: AIUsageEntry[]): number =>
  entries.reduce((s, e) => s + (e.tokensUsed ?? 0), 0);

const inWindow = (entries: AIUsageEntry[], windowMs: number): AIUsageEntry[] => {
  const now = Date.now();
  return entries.filter((e) => now - e.ts <= windowMs);
};

export const computeAICostStats = (): AICostStats => {
  const all = readAIUsageLog();
  const today = inWindow(all, DAY_MS);
  const week = inWindow(all, WEEK_MS);
  const month = inWindow(all, MONTH_MS);

  const totalCalls = all.length;
  const totalCallsToday = today.length;
  const totalCallsMonth = month.length;
  const totalTokens = sumTokens(all);
  const todayTokens = sumTokens(today);
  const monthTokens = sumTokens(month);

  const estDailyCostUsd = (todayTokens / 1000) * BLENDED_COST_PER_1K_USD;
  const estMonthlyCostUsd = (monthTokens / 1000) * BLENDED_COST_PER_1K_USD;

  const recent = inWindow(all, WEEK_MS);
  const failed = recent.filter((e) => !e.ok).length;
  const failureRatePct =
    recent.length === 0 ? 0 : Math.round((failed / recent.length) * 100);

  const avgDurationMs =
    recent.length === 0
      ? 0
      : Math.round(recent.reduce((s, e) => s + e.durationMs, 0) / recent.length);

  const errorCounts: Record<string, number> = {};
  recent
    .filter((e) => !e.ok && e.code)
    .forEach((e) => {
      const k = e.code as string;
      errorCounts[k] = (errorCounts[k] ?? 0) + 1;
    });
  const topErrorCode =
    Object.entries(errorCounts).sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;

  // Spike detection: today vs prior 6-day average
  const last7 = week.length;
  const prior6Avg = Math.max(0, (last7 - todayTokens) / 6);
  const spikeDetected =
    totalCallsToday > 0 && totalCallsToday > prior6Avg * AI_USAGE_SPIKE_MULTIPLIER;

  const warnings: AICostWarning[] = [];
  if (estDailyCostUsd >= AI_COST_DAILY_WARN_USD) {
    warnings.push({
      level: estDailyCostUsd >= AI_COST_DAILY_WARN_USD * 2 ? 'critical' : 'warn',
      code: 'daily_cost',
      message: `Estimated daily AI cost ~$${estDailyCostUsd.toFixed(3)} exceeds the $${AI_COST_DAILY_WARN_USD.toFixed(2)} advisory threshold.`,
    });
  }
  if (estMonthlyCostUsd >= AI_COST_MONTHLY_WARN_USD) {
    warnings.push({
      level: estMonthlyCostUsd >= AI_COST_MONTHLY_WARN_USD * 2 ? 'critical' : 'warn',
      code: 'monthly_cost',
      message: `Estimated monthly AI cost ~$${estMonthlyCostUsd.toFixed(2)} exceeds the $${AI_COST_MONTHLY_WARN_USD.toFixed(2)} advisory threshold.`,
    });
  }
  if (recent.length >= 10 && failureRatePct >= AI_FAILURE_RATE_WARN_PCT) {
    warnings.push({
      level: failureRatePct >= AI_FAILURE_RATE_WARN_PCT * 2 ? 'critical' : 'warn',
      code: 'failure_rate',
      message: `AI failure rate is ${failureRatePct}% over the last 7 days (top error: ${topErrorCode ?? 'unknown'}).`,
    });
  }
  if (spikeDetected) {
    warnings.push({
      level: 'warn',
      code: 'usage_spike',
      message: `Today's AI call volume (${totalCallsToday}) is unusually high vs the prior 6-day average.`,
    });
  }

  return {
    totalCalls,
    totalCallsToday,
    totalCallsMonth,
    totalTokens,
    estDailyCostUsd,
    estMonthlyCostUsd,
    failureRatePct,
    avgDurationMs,
    topErrorCode,
    spikeDetected,
    warnings,
  };
};

/** Estimate the per-1K-token blended cost. Exposed for the diagnostics card. */
export const getBlendedCostPer1KUsd = (): number => BLENDED_COST_PER_1K_USD;
